import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { computeRiskScore } from '@/services/db/riskScore';
import type { DSRDetailReport } from '@/services/db/dsrDetail';

type Level = 'low' | 'medium' | 'high' | 'critical';

const SCORE_MAX = 100;

const RISK_BADGE: Record<Level, { className: string; label: string }> = {
  low: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Low' },
  medium: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Medium' },
  high: { className: 'bg-mozaic-red/15 text-mozaic-red', label: 'High' },
  critical: { className: 'bg-mozaic-red/20 text-mozaic-red font-bold', label: 'Critical' },
};

interface Props {
  report: DSRDetailReport;
  canRecompute: boolean;
}

export default function RiskScoreCard({ report, canRecompute }: Props) {
  const [howOpen, setHowOpen] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      computeRiskScore({ dsrId: report.id, tenantId: report.tenant_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsr_detail', report.id] });
    },
  });

  const level = report.overall_risk_level;
  const score = report.risk_score;
  const breakdown = report.risk_score_breakdown;
  const overridden = !!report.risk_level_override;
  const computedAt = report.risk_score_computed_at;
  const assessmentNote = report.risk_assessment_note;
  const recommendationNote = report.risk_recommendation_note;

  // Not yet scored — show the original one-line prompt with a compute
  // action. SBAR zones aren't meaningful before a score exists.
  if (level === null || score === null) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4" />
            Composite risk score has not been computed for this report yet.
          </div>
          {canRecompute && (
            <Button
              size="sm"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              <RefreshCw className={`mr-1.5 h-4 w-4 ${mutation.isPending ? 'animate-spin' : ''}`} />
              Compute now
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const badge = RISK_BADGE[level];

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        {/* ── S — SITUATION ─────────────────────────────────── */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              S &mdash; Situation
            </h3>
            {canRecompute && (
              <Button
                size="sm"
                variant="ghost"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                <RefreshCw className={`mr-1.5 h-4 w-4 ${mutation.isPending ? 'animate-spin' : ''}`} />
                Recalculate
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge className={badge.className}>{badge.label}</Badge>
            <span className="text-sm font-medium">
              {score}/{SCORE_MAX} pts
            </span>
            {overridden && (
              <Badge variant="outline" className="border-mozaic-blue text-mozaic-blue">
                Manually set
              </Badge>
            )}
            {computedAt && (
              <span className="text-xs text-muted-foreground">
                Computed {format(new Date(computedAt), "MMM d, yyyy 'at' HH:mm")}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={() => setHowOpen((v) => !v)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {howOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            How is this calculated?
          </button>

          {howOpen && (
            <div className="space-y-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
              <p className="font-medium">How the risk score is calculated</p>
              <p>We combine four bands of evidence from this DSR:</p>
              <ul className="ml-4 list-disc space-y-1">
                <li>
                  <strong>Activities</strong> (up to 40 pts) — critical / high-risk
                  activities, high-hazard tags (hot work, confined space,
                  critical lift, high pressure), and JSA quality.
                </li>
                <li>
                  <strong>Safety events</strong> (up to 35 pts) — the most severe event
                  type reported drives this band, with bonuses if an SWA
                  was filed.
                </li>
                <li>
                  <strong>Workforce</strong> (up to 15 pts) — elevated SSE% on the crew,
                  weighted to new-to-industry personnel.
                </li>
                <li>
                  <strong>Permits</strong> (up to 10 pts) — active hot work, confined
                  space, critical lift, and any suspensions.
                </li>
              </ul>
              <p>
                Your score <strong>{score}/{SCORE_MAX}</strong> falls into
                the <strong>{badge.label}</strong> band
                (0–19 Low • 20–39 Medium • 40–59 High • 60+ Critical).
                The specific signals that contributed are listed in
                Background below.
              </p>
              <p className="text-xs text-muted-foreground">
                An SSR can override the computed level in the Assessment
                section, with a justification.
              </p>
            </div>
          )}
        </div>

        {/* ── B — BACKGROUND ────────────────────────────────── */}
        <div className="space-y-2 border-t pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            B &mdash; Background
          </h3>

          {breakdown ? (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 rounded-md bg-muted/50 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Activity signals</span>
                <span className="text-right font-mono">+{breakdown.activity_points}</span>
                <span className="text-muted-foreground">Safety event signals</span>
                <span className="text-right font-mono">+{breakdown.safety_event_points}</span>
                <span className="text-muted-foreground">Workforce signals</span>
                <span className="text-right font-mono">+{breakdown.workforce_points}</span>
                <span className="text-muted-foreground">Permit signals</span>
                <span className="text-right font-mono">+{breakdown.permit_points}</span>
                <span className="border-t pt-1 font-medium">Total score</span>
                <span className="border-t pt-1 text-right font-mono font-semibold">
                  {breakdown.total}/{SCORE_MAX}
                </span>
              </div>

              {breakdown.signals.length > 0 ? (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                    Signals fired
                  </p>
                  <ul className="ml-4 list-disc space-y-0.5 text-sm">
                    {breakdown.signals.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No risk signals fired on this report.
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Per-band breakdown is unavailable for this report.
            </p>
          )}
        </div>

        {/* ── A — ASSESSMENT ────────────────────────────────── */}
        <div className="space-y-2 border-t pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            A &mdash; Assessment
          </h3>

          {overridden && (
            <div className="rounded-md border border-mozaic-blue/30 bg-mozaic-blue/5 px-3 py-2 text-sm">
              <span className="font-medium text-mozaic-blue">
                Manually set to {level}
              </span>
              {report.risk_level_override_reason && (
                <> &mdash; {report.risk_level_override_reason}</>
              )}
            </div>
          )}

          {assessmentNote ? (
            <p className="whitespace-pre-wrap text-sm">{assessmentNote}</p>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              No assessment recorded.
            </p>
          )}
        </div>

        {/* ── R — RECOMMENDATION ────────────────────────────── */}
        <div className="space-y-2 border-t pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            R &mdash; Recommendation
          </h3>

          {recommendationNote ? (
            <p className="whitespace-pre-wrap text-sm">{recommendationNote}</p>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              No recommendation recorded.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
